/**
 * Patient Form Component
 * Form for creating and editing patients with validation
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2 } from 'lucide-react';
import type { DentalOfficePatient } from '../../types';
import { isValidKennitala } from '../../lib/utils';

// Validation schema
const patientSchema = z.object({
  first_name: z.string().min(1, 'Fornafn er skyldubundið').max(100),
  last_name: z.string().min(1, 'Eftirnafn er skyldubundið').max(100),
  kennitala: z
    .string()
    .min(1, 'Kennitala er skyldubundin')
    .refine((val) => isValidKennitala(val), {
      message: 'Ógild kennitala (sniðmát: DDMMYY-XXXX)',
    }),
  email: z.string().email('Ógilt netfang').optional().or(z.literal('')),
  phone: z.string().min(1, 'Símanúmer er skyldubundið'),
  date_of_birth: z.string().min(1, 'Fæðingardagur er skyldubundinn'),
  address: z.string().max(500).optional(),
  city: z.string().max(100).optional(),
  postal_code: z.string().max(10).optional(),
  emergency_contact_name: z.string().max(200).optional(),
  emergency_contact_phone: z.string().optional(),
  allergies: z.string().max(1000).optional(),
  medical_conditions: z.string().max(2000).optional(),
  current_medications: z.string().max(2000).optional(),
  insurance_provider: z.string().max(200).optional(),
  insurance_number: z.string().max(100).optional(),
  notes: z.string().max(5000).optional(),
  preferred_language: z.enum(['is', 'en']).default('is'),
});

export type PatientFormData = z.infer<typeof patientSchema>;

interface PatientFormProps {
  patient?: DentalOfficePatient;
  onSubmit: (data: PatientFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode?: 'create' | 'edit';
}

export function PatientForm({
  patient,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode = 'create',
}: PatientFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: patient
      ? {
          ...patient,
          email: patient.email || '',
          address: patient.address || '',
          city: patient.city || '',
          postal_code: patient.postal_code || '',
          emergency_contact_name: patient.emergency_contact_name || '',
          emergency_contact_phone: patient.emergency_contact_phone || '',
          allergies: patient.allergies || '',
          medical_conditions: patient.medical_conditions || '',
          current_medications: patient.current_medications || '',
          insurance_provider: patient.insurance_provider || '',
          insurance_number: patient.insurance_number || '',
          notes: patient.notes || '',
        }
      : {
          preferred_language: 'is',
        },
  });

  useEffect(() => {
    if (patient) {
      reset({
        ...patient,
        email: patient.email || '',
        address: patient.address || '',
        city: patient.city || '',
        postal_code: patient.postal_code || '',
        emergency_contact_name: patient.emergency_contact_name || '',
        emergency_contact_phone: patient.emergency_contact_phone || '',
        allergies: patient.allergies || '',
        medical_conditions: patient.medical_conditions || '',
        current_medications: patient.current_medications || '',
        insurance_provider: patient.insurance_provider || '',
        insurance_number: patient.insurance_number || '',
        notes: patient.notes || '',
      });
    }
  }, [patient, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          {mode === 'create' ? 'Nýr sjúklingur' : 'Breyta sjúklingi'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Basic Information */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 mb-4">Grunnupplýsingar</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
              Fornafn <span className="text-error">*</span>
            </label>
            <input
              {...register('first_name')}
              type="text"
              id="first_name"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                errors.first_name ? 'border-error' : 'border-gray-300'
              }`}
            />
            {errors.first_name && (
              <p className="mt-1 text-sm text-error">{errors.first_name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
              Eftirnafn <span className="text-error">*</span>
            </label>
            <input
              {...register('last_name')}
              type="text"
              id="last_name"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                errors.last_name ? 'border-error' : 'border-gray-300'
              }`}
            />
            {errors.last_name && (
              <p className="mt-1 text-sm text-error">{errors.last_name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="kennitala" className="block text-sm font-medium text-gray-700 mb-1">
              Kennitala <span className="text-error">*</span>
            </label>
            <input
              {...register('kennitala')}
              type="text"
              id="kennitala"
              placeholder="DDMMYY-XXXX"
              className={`w-full px-4 py-2 border rounded-lg font-mono focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                errors.kennitala ? 'border-error' : 'border-gray-300'
              }`}
            />
            {errors.kennitala && (
              <p className="mt-1 text-sm text-error">{errors.kennitala.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
              Fæðingardagur <span className="text-error">*</span>
            </label>
            <input
              {...register('date_of_birth')}
              type="date"
              id="date_of_birth"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                errors.date_of_birth ? 'border-error' : 'border-gray-300'
              }`}
            />
            {errors.date_of_birth && (
              <p className="mt-1 text-sm text-error">{errors.date_of_birth.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 mb-4">Tengiliðaupplýsingar</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Símanúmer <span className="text-error">*</span>
            </label>
            <input
              {...register('phone')}
              type="tel"
              id="phone"
              placeholder="555-1234"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                errors.phone ? 'border-error' : 'border-gray-300'
              }`}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-error">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Netfang
            </label>
            <input
              {...register('email')}
              type="email"
              id="email"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                errors.email ? 'border-error' : 'border-gray-300'
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error">{errors.email.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Heimilisfang
            </label>
            <input
              {...register('address')}
              type="text"
              id="address"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              Borg/Bær
            </label>
            <input
              {...register('city')}
              type="text"
              id="city"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1">
              Póstnúmer
            </label>
            <input
              {...register('postal_code')}
              type="text"
              id="postal_code"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 mb-4">Neyðartengiliður</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="emergency_contact_name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nafn
            </label>
            <input
              {...register('emergency_contact_name')}
              type="text"
              id="emergency_contact_name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="emergency_contact_phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Símanúmer
            </label>
            <input
              {...register('emergency_contact_phone')}
              type="tel"
              id="emergency_contact_phone"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Medical Information */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 mb-4">Heilsufarsupplýsingar</h3>

        <div>
          <label htmlFor="allergies" className="block text-sm font-medium text-gray-700 mb-1">
            Ofnæmi
          </label>
          <textarea
            {...register('allergies')}
            id="allergies"
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            placeholder="T.d. penisillín, latex..."
          />
        </div>

        <div>
          <label
            htmlFor="medical_conditions"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Sjúkdómar/Ástand
          </label>
          <textarea
            {...register('medical_conditions')}
            id="medical_conditions"
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            placeholder="T.d. sykursýki, háþrýstingur..."
          />
        </div>

        <div>
          <label
            htmlFor="current_medications"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Lyf
          </label>
          <textarea
            {...register('current_medications')}
            id="current_medications"
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            placeholder="Lyf sem sjúklingur tekur..."
          />
        </div>
      </div>

      {/* Insurance Information */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 mb-4">Tryggingaupplýsingar</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="insurance_provider"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Tryggingafélag
            </label>
            <input
              {...register('insurance_provider')}
              type="text"
              id="insurance_provider"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="insurance_number"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Trygginganúmer
            </label>
            <input
              {...register('insurance_number')}
              type="text"
              id="insurance_number"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Additional Notes */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 mb-4">Athugasemdir</h3>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Athugasemdir
          </label>
          <textarea
            {...register('notes')}
            id="notes"
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            placeholder="Almennar athugasemdir um sjúkling..."
          />
        </div>

        <div>
          <label
            htmlFor="preferred_language"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Tungumál
          </label>
          <select
            {...register('preferred_language')}
            id="preferred_language"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="is">Íslenska</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 justify-end pt-6 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Hætta við
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {mode === 'create' ? 'Búa til sjúkling' : 'Vista breytingar'}
        </button>
      </div>
    </form>
  );
}
